import { INITIAL_PROMPT_MESSAGE_ID_PREFIX } from '@volley/crucible-ipc-types/ipc-channels';
import type { ChatMessage } from '../chat/types';

export interface CreateHandoffRouteState {
  scaffoldFirst?: unknown;
  createInitialPrompt?: string;
  createPromptAutoKickoff?: true;
  createHandoffMode?: 'design';
}

export function buildCreateHandoffRouteState(args: {
  prompt: string;
  autoKickoff: boolean;
  scaffoldFirst?: unknown;
  mode?: CreateHandoffRouteState['createHandoffMode'];
}): CreateHandoffRouteState | undefined {
  const state: CreateHandoffRouteState = {};
  if (args.scaffoldFirst !== undefined) {
    state.scaffoldFirst = args.scaffoldFirst;
  }
  if (args.mode === 'design') {
    state.createHandoffMode = 'design';
  }

  const prompt = args.prompt.trim();
  if (args.autoKickoff && prompt.length > 0) {
    state.createInitialPrompt = prompt;
    state.createPromptAutoKickoff = true;
  }

  return Object.keys(state).length > 0 ? state : undefined;
}

export function readCreateHandoffModeFromRouteState(
  state: unknown,
): CreateHandoffRouteState['createHandoffMode'] | null {
  if (typeof state !== 'object' || state === null) return null;
  const candidate = state as { createHandoffMode?: unknown };
  return candidate.createHandoffMode === 'design' ? 'design' : null;
}

export function readCreateInitialPromptFromRouteState(state: unknown): string | null {
  if (typeof state !== 'object' || state === null) return null;
  const candidate = state as {
    createInitialPrompt?: unknown;
    createPromptAutoKickoff?: unknown;
  };
  if (candidate.createPromptAutoKickoff !== true) return null;
  if (typeof candidate.createInitialPrompt !== 'string') return null;
  const prompt = candidate.createInitialPrompt.trim();
  return prompt.length > 0 ? prompt : null;
}

export function buildCreateInitialPromptMessage(
  prompt: string,
  timestamp = Date.now(),
): ChatMessage | null {
  const trimmed = prompt.trim();
  if (trimmed.length === 0) return null;
  return {
    id: `${INITIAL_PROMPT_MESSAGE_ID_PREFIX}${timestamp}`,
    role: 'user',
    content: trimmed,
    timestamp,
  };
}

export function applyCreateInitialPromptFallback(args: {
  loadedMessages: ChatMessage[];
  agentInFlight: boolean;
  routePrompt: string | null;
  now?: () => number;
}): { messages: ChatMessage[]; applied: boolean } {
  if (args.agentInFlight || args.loadedMessages.length > 0 || !args.routePrompt) {
    return { messages: args.loadedMessages, applied: false };
  }

  const message = buildCreateInitialPromptMessage(args.routePrompt, (args.now ?? Date.now)());
  if (!message) return { messages: args.loadedMessages, applied: false };
  return { messages: [message], applied: true };
}
